import { useState } from 'react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    useProposalOutcomes,
    useRefreshSavedNotes,
} from '@apps/web/components/phoebe_chat_view/artifacts/saved_note_outcomes_context';
import { type NoteOutcome } from '@apps/web/lib/agent_note_outcome_copy';
import { toast } from '@apps/web/lib/app_notifications';
import { resolveAgentNoteProposal } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export type ApprovalDockReceipt = {
    readonly itemId: string;
    readonly action: 'approve' | 'reject';
    readonly text: string;
    readonly domain: ApprovalDockProposal['domain'];
};

export type ApprovalDockProposal = {
    readonly noteId: string;
    readonly itemId: string;
    readonly text: string;
    readonly domain: NoteOutcome['domain'];
    readonly entity: {
        readonly type: 'caregiver' | 'client';
        readonly id: string;
        readonly name: string;
    } | null;
};

export type ApprovalDockPendingDecision = {
    readonly itemId: string;
    readonly action: 'approve' | 'reject';
};

/**
 * Controller for the approval dock: the open proposals, the receipts left
 * by resolving them, and the resolve command.
 *
 * Receipts are per-dock-instance state; the dock is keyed on the
 * conversation in chat_view, so switching conversations remounts it and a
 * receipt never outlives the chat it belongs to.
 */
export function useApprovalDock() {
    const proposals = useProposalOutcomes();
    const refresh = useRefreshSavedNotes();
    const { organizationId } = usePhoebeChatOrganizationScope();
    const [pendingDecision, setPendingDecision] =
        useState<ApprovalDockPendingDecision | null>(null);
    const [receipts, setReceipts] = useState<
        ReadonlyArray<ApprovalDockReceipt>
    >([]);

    // A proposal already collapsed to a receipt must not re-render as
    // actionable while the notes poll catches up with the resolution.
    const receiptItemIds = new Set(receipts.map((receipt) => receipt.itemId));
    const open: ReadonlyArray<ApprovalDockProposal> = proposals
        .filter(({ outcome }) => !receiptItemIds.has(outcome.itemId))
        .map(({ noteId, outcome }) => ({
            noteId,
            itemId: outcome.itemId,
            text: outcome.text,
            domain: outcome.domain,
            entity: outcome.entity,
        }));

    const resolve = async (
        proposal: ApprovalDockProposal,
        action: 'approve' | 'reject',
    ) => {
        if (organizationId === null) {
            return;
        }
        setPendingDecision({ itemId: proposal.itemId, action });
        try {
            await resolveAgentNoteProposal({
                organizationId,
                noteId: proposal.noteId,
                itemId: proposal.itemId,
                action,
            });
            setReceipts((previous) => [
                ...previous,
                {
                    itemId: proposal.itemId,
                    action,
                    text: proposal.text,
                    domain: proposal.domain,
                },
            ]);
        } catch {
            toast.error(
                action === 'approve'
                    ? 'We couldn’t approve this. Please try again.'
                    : 'We couldn’t reject this. Please try again.',
                { persistNotification: false },
            );
        }
        // Refetch outside the guard above: on success it shows the new state;
        // on failure it converges a proposal another tab already resolved.
        await refresh().catch(() => undefined);
        setPendingDecision(null);
    };

    return {
        isVisible:
            organizationId !== null && (open.length > 0 || receipts.length > 0),
        open,
        receipts,
        pendingDecision,
        resolve,
    };
}
